import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowRight, Play, Flame, Gem, Wand2, Sparkles, Phone } from "lucide-react";
import AnimatedBookButton from "@/components/AnimatedBookButton";
import { Eyebrow, Reveal, Stagger, StaggerItem, SectionHeading, Marquee } from "@/components/Reveal";
import OrnateFrame from "@/components/OrnateFrame";
import { lazy, Suspense } from "react";
import { IMAGES, RASHI_NAMES, BLOGS, CLIENT_FALLBACK } from "@/data/content";
import { SITE, STATS } from "@/config";

const FreeKundliSection = lazy(() => import("@/components/FreeKundliSection"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ease = [0.22, 1, 0.36, 1];

function HeroLine({ children, delay }) {
  return (
    <span>
      <motion.span
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease }}
        className="block"
      >
        {children}
      </motion.span>
    </span>
  );
}

function RashiRing() {
  const text = RASHI_NAMES.join("  ·  ").toUpperCase() + "  ·  ";
  return (
    <svg viewBox="0 0 300 300" className="w-full h-full animate-spin-slower" aria-hidden="true">
      <defs>
        <path id="rashi-circle" d="M150,150 m-128,0 a128,128 0 1,1 256,0 a128,128 0 1,1 -256,0" fill="none" />
      </defs>
      <circle cx="150" cy="150" r="128" fill="none" stroke="#E8B57A" strokeWidth="0.8" />
      <circle cx="150" cy="150" r="112" fill="none" stroke="#E8B57A" strokeWidth="0.5" strokeDasharray="2 6" />
      <text fill="#B45309" fontSize="13.5" letterSpacing="4.5" style={{ fontFamily: "Cinzel, serif" }}>
        <textPath href="#rashi-circle">{text}</textPath>
      </text>
      {RASHI_NAMES.map((_, i) => {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
        return <circle key={i} cx={150 + Math.cos(a) * 128} cy={150 + Math.sin(a) * 128} r="2.4" fill="#FF6B00" />;
      })}
    </svg>
  );
}

function Horoscope() {
  const [signs, setSigns] = useState(RASHI_NAMES.map((s) => CLIENT_FALLBACK(s)));
  const [active, setActive] = useState(0);
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

  useEffect(() => {
    fetch(`${API}/daily-horoscope`)
      .then((r) => r.json())
      .then((d) => d.signs && setSigns(d.signs))
      .catch(() => {});
  }, []);

  const s = signs[active];
  const Bar = ({ label, v }) => (
    <div>
      <div className="flex justify-between text-xs text-[#8A7364] mb-1.5">
        <span className="uppercase tracking-widest">{label}</span>
        <span className="font-semibold text-maroon">{v}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#F0DDBE] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${v}%` }}
          transition={{ duration: 0.9, ease }}
          className="h-full rounded-full"
          style={{ background: "linear-gradient(90deg,#FF9140,#EA580C)" }}
        />
      </div>
    </div>
  );

  return (
    <div data-testid="daily-horoscope-widget" className="card-ornate rounded-3xl p-6 sm:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <Eyebrow>Daily Vedic Horoscope</Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-maroon mt-2">Today the Stars Say</h2>
        </div>
        <p className="text-sm text-[#8A7364]" data-testid="horoscope-date">{today}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2 mb-6">
        {RASHI_NAMES.map((r, i) => (
          <button
            key={r}
            onClick={() => setActive(i)}
            data-testid={`rashi-pill-${r.toLowerCase()}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-all duration-200 ${
              i === active
                ? "bg-maroon text-[#FDF3E3] border-maroon font-medium"
                : "bg-white/70 border-[#E8B57A] text-[#6B5D52] hover:border-[#FF6B00] hover:text-saffron"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8" data-testid="horoscope-panel">
        <div className="space-y-4">
          <Bar label="Finance" v={s.finance} />
          <Bar label="Love" v={s.love} />
          <Bar label="Health" v={s.health} />
          <Bar label="Overall" v={s.overall} />
          <div className="flex flex-wrap gap-2 pt-2">
            <span className="text-xs bg-[#FCE7D0] border border-[#F0C48F] text-maroon rounded-full px-3 py-1.5" data-testid="horoscope-lucky-number">Lucky numbers — {s.luckyNumber}</span>
            <span className="text-xs bg-[#FCE7D0] border border-[#F0C48F] text-maroon rounded-full px-3 py-1.5" data-testid="horoscope-lucky-color">{s.luckyColor}</span>
            <span className="text-xs bg-[#FCE7D0] border border-[#F0C48F] text-maroon rounded-full px-3 py-1.5" data-testid="horoscope-mood">{s.mood}</span>
          </div>
        </div>
        <div className="bg-white/70 border border-[#F0C48F] rounded-2xl p-5">
          <p className="font-deva text-saffron text-lg" data-testid="horoscope-mantra">{s.mantra}</p>
          <p className="text-[#44403C] text-[15px] leading-relaxed mt-3" data-testid="horoscope-overview">{s.overview}</p>
          <div className="h-px bg-[#F0C48F] my-4" />
          <p className="text-sm text-[#6B5D52]"><span className="font-semibold text-maroon">Tip of the day — </span>{s.tip}</p>
        </div>
      </div>
    </div>
  );
}

const JOURNEY = [
  {
    icon: Flame,
    title: "One-on-One Consultation",
    desc: "Speak directly with the Messenger — chart-based clarity for career, love, money and the decisions that cannot wait.",
    to: "/the-call-consultation",
    img: IMAGES.diyaWoman,
    cta: "Book a Session",
  },
  {
    icon: Wand2,
    title: "Innovative Astrology Tools",
    desc: "Modern instruments for ancient wisdom — daily horoscope, rashi harmony checks and precision kundli instruments.",
    to: "/tools",
    img: IMAGES.celestialClock,
    cta: "Try the Tools",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yPortrait = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-[84px] lg:pt-[120px]">
        <motion.div style={{ y: yGlow }} className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#FF6B00]/15 blur-[110px]" />
        <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-[#F59E0B]/10 blur-[100px]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-20 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <Reveal y={12}>
              <p className="font-deva text-saffron text-lg tracking-wide">ॐ नमः शिवाय</p>
              <Eyebrow>Vedic Jyotish · Kundli · Karmic Remedies</Eyebrow>
            </Reveal>
            <h1 data-testid="hero-heading" className="mask-lines font-display font-semibold text-maroon text-[42px] leading-[1.05] sm:text-6xl lg:text-[68px] mt-5">
              <HeroLine delay={0.15}>Every Question</HeroLine>
              <HeroLine delay={0.3}>Has an Answer</HeroLine>
              <HeroLine delay={0.45}>
                <em className="text-gradient-saffron">Written in the Stars.</em>
              </HeroLine>
            </h1>
            <Reveal delay={0.65} y={16}>
              <p className="text-[#6B5D52] text-lg leading-relaxed mt-6 max-w-lg">
                Vedic astrology, precise kundli readings and karmic remedies — offered by the{" "}
                <span className="text-maroon font-semibold">Messenger of Shiva</span> for seekers in {STATS.countries} countries.
              </p>
            </Reveal>
            <Reveal delay={0.8}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <AnimatedBookButton to="/the-call-consultation" testid="hero-consult-cta">
                  <Phone size={18} /> Book Your 1:1 Call Now <ArrowRight size={18} />
                </AnimatedBookButton>
              </div>
            </Reveal>
          </div>

          <motion.div style={{ y: yPortrait }} className="relative mx-auto w-full max-w-[320px] sm:max-w-[420px]">
            <div className="absolute inset-0 -m-5 sm:-m-8"><RashiRing /></div>
            <div className="relative rounded-full overflow-hidden shadow-[0_40px_100px_-30px_rgba(86,20,24,0.6)] border-4 border-[#E8B57A]/70 aspect-square max-w-[360px] mx-auto">
              <img src={IMAGES.sagePortrait} alt="Messenger of Shiva" fetchPriority="high" decoding="async" className="w-full h-full object-cover" data-testid="hero-portrait" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#561418]/30 to-transparent" />
            </div>
            <div className="animate-float absolute -bottom-2 left-1/2 -translate-x-1/2 bg-maroon text-[#FDF3E3] font-cinzel text-[11px] tracking-[0.25em] uppercase px-6 py-3 rounded-full shadow-xl whitespace-nowrap">
              Your Cosmic Guide
            </div>
          </motion.div>
        </div>
      </section>

      {/* RATINGS BAND */}
      <section className="border-y border-[#F0C48F] bg-[#FDF3E3]/70">
        <Stagger className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { v: `${STATS.rating}`, l: "Seeker Rating" },
            { v: STATS.charts, l: "Charts Read" },
            { v: STATS.countries, l: "Countries Served" },
            { v: `${STATS.community}`, l: "Community Blessed" },
          ].map((s, i) => (
            <StaggerItem key={i} className="text-center">
              <p className="font-display text-3xl sm:text-4xl font-bold text-maroon" data-testid={`stat-${i}`}>
                {s.v} {i === 0 && <Star size={18} className="inline text-[#F59E0B]" fill="currentColor" />}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#8A7364] mt-1.5">{s.l}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <Marquee items={["Om Namah Shivaya", "Vedic Kundli Precision", "Mahadev Karmic Remedies", "Ratna Chikitsa", "Dosha Nivaran", "Shubh Muhurat"]} />

      {/* CONSULTATION TEASER */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            eyebrow="Consultation"
            title="Real Astrologers. Real Conversations. Real Remedies."
            sub="One-on-one sessions with the Messenger of Shiva — for career, marriage, money, health, and the decisions that cannot wait."
          />
          <Stagger className="grid lg:grid-cols-2 gap-5 mt-12">
            <StaggerItem>
              <Link to="/the-call-consultation" data-testid="teaser-card-main" className="group relative block h-[300px] rounded-3xl overflow-hidden">
                <img loading="lazy" decoding="async" src={IMAGES.diyaLamps} alt="Consultation" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A0C]/85 via-[#2A0A0C]/20 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <h3 className="font-display text-3xl text-[#FDF3E3]">The Messenger's Consultation</h3>
                  <p className="text-[#F3D9BC] mt-2 max-w-sm">Speak directly. Chart explained in front of you, remedies given instantly.</p>
                  <span className="sl-book-wrap mt-5 inline-flex">
                    <span className="sl-book-btn btn-gold inline-flex items-center gap-2 font-bold px-6 py-2.5 rounded-full text-sm">
                      Book Your 1:1 Call Now <ArrowRight size={16} />
                    </span>
                  </span>
                </div>
              </Link>
            </StaggerItem>
            {[
              { img: IMAGES.shivaMeditate, t: "Reports That Read Like Maps", d: "Kundli, career, love and wealth — precise PDF reports.", to: "/reports" },
            ].map((c, i) => (
              <StaggerItem key={i}>
                <Link to={c.to} data-testid={`teaser-card-${i}`} className="group relative block h-[300px] rounded-3xl overflow-hidden">
                  <img loading="lazy" decoding="async" src={c.img} alt={c.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#2A0A0C]/80 to-transparent" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-center">
                    <h3 className="font-display text-2xl text-[#FDF3E3]">{c.t}</h3>
                    <p className="text-[#F3D9BC] text-sm mt-1.5 max-w-xs">{c.d}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FREE KUNDLI */}
      <Suspense fallback={<div className="h-[520px] bg-[#FAF7F2]" />}>
        <FreeKundliSection />
      </Suspense>

      {/* HOROSCOPE */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal><Horoscope /></Reveal>
        </div>
      </section>

      {/* JOURNEY TRIO */}
      <section className="py-24 bg-[#FDF3E3]/60 border-y border-[#F0C48F]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            eyebrow="Your Path"
            title="Everything You Need for Your Spiritual Journey"
            sub="Two sacred doors — walk through the one your chart is pointing to."
          />
          <Stagger className="grid md:grid-cols-2 gap-6 mt-12">
            {JOURNEY.map((j, i) => (
              <StaggerItem key={i}>
                <Link to={j.to} data-testid={`journey-card-${j.title.toLowerCase().replace(/\s/g, "-")}`} className="group card-ornate rounded-3xl overflow-hidden flex flex-col h-full">
                  <div className="relative h-52 overflow-hidden">
                    <img loading="lazy" decoding="async" src={j.img} alt={j.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A0C]/60 to-transparent" />
                    <div className="absolute bottom-4 left-5 w-11 h-11 rounded-full bg-[#FDF3E3] grid place-items-center shadow-lg">
                      <j.icon className="text-saffron" size={20} />
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display text-2xl font-semibold text-maroon">{j.title}</h3>
                    <p className="text-[#6B5D52] text-[15px] leading-relaxed mt-2 flex-1">{j.desc}</p>
                    <span className="inline-flex items-center gap-2 mt-4 text-saffron font-semibold text-sm group-hover:gap-3.5 transition-all">
                      {j.cta} <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SPOTLIGHT */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            eyebrow="In the Spotlight"
            title="Wisdom, Spoken Aloud"
            sub="Watch the Messenger break down real questions about love, career, and the year ahead."
          />
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { img: IMAGES.shivaMeditate, t: "Secrets Hidden in Your Stars" },
              { img: IMAGES.celestialClock, t: "Astrology for Career Choices" },
              { img: IMAGES.shivaSunset, t: "Is Your Astrologer Real?" },
              { img: IMAGES.diyaWoman, t: "Shravan Special — What Not to Do" },
            ].map((v, i) => (
              <StaggerItem key={i}>
                <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid={`spotlight-card-${i}`} className="group relative block rounded-2xl overflow-hidden aspect-[4/3]">
                  <img loading="lazy" decoding="async" src={v.img} alt={v.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A0A0C]/80 via-transparent to-transparent" />
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="w-12 h-12 rounded-full bg-[#FDF3E3]/90 grid place-items-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                      <Play size={18} className="text-maroon ml-0.5" fill="currentColor" />
                    </span>
                  </div>
                  <p className="absolute bottom-4 left-4 right-4 text-[#FDF3E3] text-sm font-medium leading-snug">{v.t}</p>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* BLOGS PREVIEW */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            eyebrow="Sacred Wisdom"
            title="From the Journal"
            sub="Ancient insight, written for modern karma."
          />
          <Stagger className="grid md:grid-cols-3 gap-6 mt-12">
            {BLOGS.map((b, i) => (
              <StaggerItem key={b.slug}>
                <Link to={`/blogs/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group card-ornate rounded-3xl overflow-hidden flex flex-col h-full">
                  <div className="h-44 overflow-hidden">
                    <img loading="lazy" decoding="async" src={b.img} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="text-xs uppercase tracking-[0.2em] text-saffron font-semibold">{b.date}</p>
                    <h3 className="font-display text-xl font-semibold text-maroon mt-2 leading-snug">{b.title}</h3>
                    <p className="text-[#6B5D52] text-sm mt-2 flex-1">{b.excerpt}</p>
                    <span className="inline-flex items-center gap-2 mt-4 text-saffron font-semibold text-sm">
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="text-center mt-10">
            <Link to="/blogs" data-testid="view-all-blogs" className="inline-flex items-center gap-2 text-maroon font-semibold border border-[#E8B57A] rounded-full px-7 py-3 hover:bg-maroon hover:text-[#FDF3E3] transition-colors">
              View the Full Journal <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 overflow-hidden grain" style={{ background: "linear-gradient(120deg,#7A1F10,#882414)" }}>
        <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full bg-[#FF6B00]/25 blur-[100px]" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
          <Reveal>
            <Sparkles className="mx-auto text-[#F59E0B] mb-6" size={34} />
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#FDF3E3] leading-tight">
              The stars have waited lifetimes to tell you this.
            </h2>
            <p className="text-[#F3D9BC] mt-5 text-lg">One conversation. One chart. One clear path forward.</p>
            <div className="mt-9 flex justify-center">
              <AnimatedBookButton to="/the-call-consultation" testid="final-cta-consultation">
                <Phone size={18} /> Book Your 1:1 Call Now <ArrowRight size={18} />
              </AnimatedBookButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
